<script setup lang="ts">
import { StatsigSingleton } from '@laam/statsig-client';
import { useRuntimeConfig } from 'nuxt/app';
import DeliveryOptions from '~/components/delivery-options/DeliveryOptions.vue';
import MobileHeader from '~/components/MobileHeader.vue';
import ProductDetailV2 from '~/components/product-detail-v2/index.vue';
import ProductDetail from '~/components/product-detail/index.vue';
import type { BannerReasons } from '~/components/TopBanner.vue';
import { useFetchProductByHandle } from '~/data/product';
import {
	isMobileView,
	productTitle,
	videosSchemaValues,
} from '~/utils/helpers';
import useSeoSchema from '~/utils/inject-webpage-schema';
import { metaDesc, metaTitle } from '~/utils/seo';

const SearchButton = defineAsyncComponent(
	() => import('../../components/SearchButton.vue'),
);

const ShareDialog = defineAsyncComponent(
	() => import('../../components/product-detail/ShareDialog.vue'),
);

const ShareDialogV2 = defineAsyncComponent(
	() => import('../../components/product-detail-v2/ShareDialog.vue'),
);

const PdpCarousel = defineAsyncComponent(
	() => import('../../components/product-detail/PdpCarousel.vue'),
);

interface DynamicCarousel {
	title: string;
	key: string;
	show: boolean;
}

const { storeType, store } = useGlobalStore();
const { pdpRedesignExperiment } = useStatsigExperiment();

const route = useRoute();
const dynamicCarousels = ref<DynamicCarousel[]>([]);
const enabled = ref(true);
const LoganStore = useLoganStore();
const { accessToken } = storeToRefs(LoganStore);
// passing accessToken as queryKey to refetch data once logan token is available on client side
const { data, error, suspense } = await useFetchProductByHandle(
	route.params.handle as string,
	enabled,
	accessToken,
);
const notifyMeProduct = ref<{
	show: boolean;
	shopifyId: number;
	url: string;
	disabled: number[];
}>({
	show: false,
	shopifyId: 0,
	url: '',
	disabled: [],
});
provide('notifyMeProduct', notifyMeProduct);

onServerPrefetch(async () => {
	await suspense();
});

await suspense();

if (error.value || !data.value) {
	throw new Error('Error fetching initial product data');
}

const title = computed(() => {
	if (data.value.node_l1 || data.value.node_l4) {
		return productTitle(data.value, true);
	} else return `${data.value.title} - ${data.value.partner}`;
});

const videoTitle = computed(() => {
	if (data.value.node_l1 || data.value.node_l4) {
		return videosSchemaValues(data.value, true);
	} else return `${data.value.title} - ${data.value.partner}`;
});

const seoTitle = computed(() => {
	return metaTitle(data, store.id, storeType!);
});

const seoDescription = computed(() => {
	return metaDesc(data, store.id, storeType!, store.name!);
});

useSeoSchema(
	'products',
	seoTitle.value,
	seoDescription.value?.replace(/\.$/, ''),
	data.value.image.src,
);
useHead({
	title: seoTitle,
	titleTemplate: null,
});

useSeoMeta({
	ogTitle: metaTitle(data, store.id, storeType!),
	description: seoDescription,
	ogDescription: seoDescription,
});

provide('seoTitle', seoTitle);
provide('seoDescription', seoDescription);

const getStatsisCarouselConfig = () => {
	const carousels = StatsigSingleton.getDynamicConfig('pdp-carousels');
	if (!carousels) {
		return;
	}
	const pdpCarousels = carousels.value.pdpCarousels as DynamicCarousel[];
	if (pdpCarousels.length > 0) {
		dynamicCarousels.value = pdpCarousels.filter(
			(carousel: DynamicCarousel) => carousel.show,
		);
	}
};

onMounted(() => {
	callWithRetries(() => getStatsisCarouselConfig());
});

const visible = inject('headerVisible') as Ref<boolean>;
const topBannerReason = inject('topBannerReason') as Ref<BannerReasons>;
const url = useRequestURL();

const stockStatus = (product: any) => {
	return product.total_inventory_quantity > 0 ? 'InStock' : 'OutOfStock';
};

const videoUrl = () => {
	const videos = data.value.content_videos?.length
		? data.value.content_videos
		: data.value.videos;

	if (videos?.length) {
		const video = videos[0];
		return video?.hls_url || video?.original_url || null;
	}
	return null;
};

const script = computed(() => {
	const env = process.server
		? process.env.NUXT_PUBLIC_ENV
		: useRuntimeConfig().public.env;

	if (env !== 'production' || storeType === 'OCTANE') return [];
	const scripts = [];
	scripts.push({
		type: 'application/ld+json',
		children: JSON.stringify({
			'@context': 'https://schema.org',
			'@type': 'Product',
			name: title.value.title ?? data.value.title,
			description: seoDescription.value,
			color: data.value.attributes['color-type'],
			image: {
				'@type': 'ImageObject',
				url:
					data.value.media[0]?.src ??
					`https://laam.pk/products/${data.value.handle}`,
				image:
					data.value.media[0]?.src ??
					`https://laam.pk/products/${data.value.handle}`,
				name: title.value.title ?? data.value.title,
				width: data.value.media[0]?.width,
				height: data.value.media[0]?.height,
			},
			keywords: Object.values(data.value.attributes),
			productID: data?.value?.shopify_id,
			sku: data.value.variants[0]?.sku,
			url: `${url.origin}${url.pathname}`,
			brand: {
				'@type': 'Brand',
				name: data.value.brand,
			},
			offers: {
				'@type': 'Offer',
				url: `https://laam.pk/products/${data.value.handle}`,
				priceCurrency: 'PKR',
				price:
					data.value.compare_at_price > 0
						? data.value.compare_at_price
						: data.value.price,
				itemCondition: 'https://schema.org/NewCondition',
				availability: `https://schema.org/${stockStatus(data.value)}`,
				seller: {
					'@type': 'Organization',
					name: 'LAAM',
				},
			},
			additionalProperty: [
				{
					'@type': 'PropertyValue',
					name: 'Available Sizes',
					value: data.value?.variants?.map((variant) => variant.title),
				},
			],
		}),
	});
	const hasContentVideos = data.value.content_videos?.length ?? 0;
	const hasProductVideos = data.value.videos?.length ?? 0;

	if (hasContentVideos || hasProductVideos) {
		scripts.push({
			type: 'application/ld+json',
			children: JSON.stringify({
				'@context': 'https://schema.org',
				'@type': 'VideoObject',
				contentUrl: videoUrl(),
				name: videoTitle.value.title ?? data.value.title,
				description: videoTitle.value.description ?? seoDescription.value,
				thumbnailUrl: data.value.content_videos?.length
					? data.value.content_videos[0]?.thumbnail
					: data.value.videos?.[0]?.thumbnail,
			}),
		});
	}

	return scripts;
});

if (storeType !== 'OCTANE') {
	useHead(() => ({
		script,
		link: [
			{
				rel: 'canonical',
				href: `https://laam.pk${route.fullPath.toLowerCase()}`,
			},
		],
	}));
}
</script>

<template>
	<div class="lg:max-w-[1032px] lg:mx-auto">
		<div
			v-if="isMobileView()"
			:data-visible="visible"
			:data-top-banner="!!topBannerReason"
			class="pdp--header-mobile slide-in-from-top lg:hidden sticky left-0 right-0 z-20 transition-all duration-200 ease-out"
		>
			<MobileHeader>
				<template #title>
					<div class="flex flex-col max-w-[110px]">
						<span class="font-semibold text-sm text-gray-900 truncate">{{
							data!.brand
						}}</span>
						<span class="font-normal text-gray-700 text-xs truncate">{{
							data!.title
						}}</span>
					</div>
				</template>

				<template #actions>
					<DeliveryOptions v-if="pdpRedesignExperiment?.showSizeForm" />
					<SearchButton />
					<ShareDialog v-if="!pdpRedesignExperiment?.showSizeForm" />
					<ShareDialogV2 v-if="pdpRedesignExperiment?.showSizeForm" />
					<CartButton />
				</template>
			</MobileHeader>
		</div>
		<ProductDetailV2
			v-if="pdpRedesignExperiment?.showSizeForm"
			:product-data="data!"
			parent="product-page"
			class="lg:mb-8xl mb-7xl"
		/>

		<ProductDetail
			v-if="!pdpRedesignExperiment?.showSizeForm"
			:product-data="data!"
			parent="product-page"
		/>

		<div v-for="(carousel, index) in dynamicCarousels" :key="index">
			<PdpCarousel
				class="mb-3xl"
				:class="
					index === dynamicCarousels!.length - 1 && isMobileView() && 'mb-14xl'
				"
				:meta="{ title: carousel.title, key: carousel.key }"
				:product-id="data?.shopify_id"
				:drop-handle="data?.drop_handle"
				:last-index="index === dynamicCarousels.length - 1"
			/>
		</div>
	</div>
</template>
<style scoped>
.pdp--header-mobile[data-visible='false'] {
	top: calc(
		0 - var(--plp-header-height-mobile) - var(--laam-shipping-banner-height)
	) !important;
}
.pdp--header-mobile[data-visible='true'][data-top-banner='true'] {
	top: var(--laam-shipping-banner-height);
}
.pdp--header-mobile[data-visible='true'][data-top-banner='false'] {
	top: 0;
}
</style>
